import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  IconButton,
  Tooltip,
  FormControlLabel,
  Switch
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";

const ConnectionEditDialog = ({
  open,
  onClose,
  connection,
  editData,
  setEditData,
  onSave,
  onDelete,
  onToggleConfirmed,
  elements
}) => {
  // Handle form field changes
  const handleNameChange = (e) => {
    setEditData({ ...editData, name: e.target.value });
  };
  
  const handleDescriptionChange = (e) => {
    setEditData({ ...editData, description: e.target.value });
  };
  
  const handleSourceChange = (e) => {
    setEditData({ ...editData, source: e.target.value });
  };
  
  const handleTargetChange = (e) => {
    setEditData({ ...editData, target: e.target.value });
  };
  
  const handleRelationshipTypeChange = (e) => {
    setEditData({ ...editData, relationshipType: e.target.value });
  };

  const commonRelationships = ["flows to", "controls"];
  
  // Ensure we have string values for source and target (not objects)
  // If source/target is an object (from API response), extract the _id
  const getStringId = (value) => {
    if (!value) return "";
    if (typeof value === 'object' && value._id) return String(value._id);
    return String(value);
  };
  
  const sourceValue = getStringId(editData?.source);
  const targetValue = getStringId(editData?.target);
  
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Connection</DialogTitle>
      <DialogContent>
        {/* Basic information section */}
        <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 1 }}>
          Basic Information
        </Typography>
        
        {/* Name field */}
        <TextField
          autoFocus
          margin="dense"
          label="Name"
          fullWidth
          value={editData?.name || ""}
          onChange={handleNameChange}
          placeholder="Enter connection name"
        />

        {/* Description field */}
        <TextField
          margin="dense"
          label="Description"
          fullWidth
          multiline
          rows={3}
          value={editData?.description || ""}
          onChange={handleDescriptionChange}
          placeholder="Enter optional description"
        />

        {/* Connection details section */}
        <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
          Connection Details
        </Typography>

        {/* Source Node dropdown */}
        <FormControl fullWidth margin="dense">
          <InputLabel>Source Node</InputLabel>
          <Select
            value={sourceValue}
            label="Source Node"
            onChange={handleSourceChange}
          >
            {elements.map((node) => (
                <MenuItem key={node._id} value={String(node._id)}>
                  {node.name ||
                    `Element at (${Math.round(node.boundingBox.x)}, ${Math.round(node.boundingBox.y)})`}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
        
        {/* Relationship Type field with suggestions */}
        <FormControl fullWidth margin="dense">
          <InputLabel>Relationship Type</InputLabel>
          <Select
            value={editData?.relationshipType || ""}
            label="Relationship Type"
            onChange={handleRelationshipTypeChange}
            inputProps={{ 
              sx: { textTransform: 'lowercase' } 
            }}
          >
            {commonRelationships.map((rel) => (
              <MenuItem key={rel} value={rel}>
                {rel}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Target Node dropdown */}
        <FormControl fullWidth margin="dense">
          <InputLabel>Target Node</InputLabel>
          <Select
            value={targetValue}
            label="Target Node"
            onChange={handleTargetChange}
          >
            {elements.map((node) => (
                <MenuItem key={node._id} value={String(node._id)}>
                  {node.name ||
                    `Element at (${Math.round(node.boundingBox.x)}, ${Math.round(node.boundingBox.y)})`}
                </MenuItem>
              ))}
          </Select>
        </FormControl>

        {/* Confirmation toggle */}
        <FormControlLabel
          control={
            <Switch
              checked={connection?.confirmed || false}
              onChange={() => onToggleConfirmed(connection)}
              color="success"
            />
          }
          label="Confirmed"
          sx={{ mt: 1 }}
        />
        
      </DialogContent>
      <DialogActions
        sx={{
          display: "flex",
          justifyContent: "space-between",
          px: 2,
          pb: 2,
        }}
      >
        <Tooltip title="Delete Connection">
          <IconButton
            onClick={onDelete}
            color="error"
            aria-label="delete"
          >
            <DeleteIcon />
          </IconButton>
        </Tooltip>
        <Box>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            onClick={onSave}
            variant="contained"
            color="primary"
            sx={{ ml: 1 }}
            disabled={!sourceValue || !targetValue}
          >
            Save
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ConnectionEditDialog;